/**
 * The buyable-product surfaces — intake, API, trust root, posture, scoreboard.
 *
 * ── What makes these pages different from the rest of the prototype ──
 *
 * Every other page in this prototype renders a fixture. These five render
 * **shipped engine code**, imported from `@excellent/core/verification-*`. The
 * predicates you see on the intake page were decided by
 * `buildWorkflowIntakeView`; the withheld API operation is withheld by
 * `describeVerificationApiSurface`; the criteria scoreboard is computed by
 * `evaluateBuyableCriteria`. If a gate over one of those modules goes green,
 * that is also a statement about what is on this screen.
 *
 * That was the load-bearing decision of the buyable-product run
 * (`VERIFICATION-BUYABLE-EXECUTION.md` §0): the prototype is a view over real
 * product code, never a mock of it. A design prototype that mocks its own engine
 * can show anything, which is worth nothing when the thing under review is
 * whether the product tells the truth.
 *
 * ── The one place a mock remains, and why it is labelled ──
 *
 * Compilation itself cannot run here. `verifier3/spec-compiler.ts` needs the
 * in-boundary local model, which is a server-side process; a browser cannot
 * reach it, and faking it silently would be precisely the overclaim this product
 * exists to refuse. So the intake page runs a **declared** stand-in that
 * produces a `CompileOutcome` from the pasted text, then feeds it through the
 * real view-model — and says so on screen, in the page, not in a comment. The
 * admission verdict, the grounding check, the oracle catalog and every refusal
 * are genuine.
 */

import { useMemo, useState } from "react";
import {
  buildWorkflowIntakeView,
  bindOracleCandidate,
  INTAKE_ORACLE_CATALOG,
  type WorkflowIntakeView,
  type IntakeCompileOutcome,
  type IntakeFailClosedReason,
} from "@excellent/core/verification-workflow-intake";
import {
  describeVerificationApiSurface,
  PUBLIC_VERIFICATION_API_ENDPOINTS,
  ENGINE_ONLY_OPERATIONS,
} from "@excellent/core/verification-api-server";
import {
  BUYABLE_CRITERIA,
  GAUNTLET_BLOCKERS,
  PENDING_GATES,
  evaluateBuyableCriteria,
  isBuyable,
  allBuyableGateNames,
  type GateOutcome,
} from "@excellent/core/verification-buyable-criteria";
import { CHAIN_ANCHOR_TARGETS } from "@excellent/core/verification-chain-anchor-display";
import { VERIFICATION_POSTURE_QUESTIONS, postureCoverage } from "@excellent/core/verification-posture-statement";
import {
  OUT_OF_BAND_KEY_SOURCES,
  packTrustRootAssuranceCeiling,
} from "@excellent/core/verification-trust-root";
import { href } from "./router";
import gateResultsArtifact from "./gate-results.json";
import {
  Badge,
  Button,
  Callout,
  Card,
  Code,
  Empty,
  Field,
  Fields,
  Grid,
  Icon,
  PageShell,
  Section,
  Stat,
  Table,
  TextArea,
  TextInput,
  Verdict,
  type Column,
} from "./ui";

// NOT imported from `@excellent/core/verification-chain-anchor`, deliberately.
//
// That module imports `verificationSha256` from `evidence-lineage.ts`, which
// imports `node:crypto` — and the shell's browser-only guard correctly refuses
// `node:crypto` in the client bundle. Importing the module for two display
// helpers therefore 500s the ENTIRE prototype, not just this page: the guard
// fires during transform, so every route dies, including the fixture pages that
// have nothing to do with anchoring. That is how it presented — a 500 on
// /receipts with an empty body — and it took a full module-graph walk to find,
// because the failing URL is a dist file nine hops from the entry.
//
// The honest sentence belongs in the engine rather than the UI, and it now
// LIVES there: `chain-anchor-display.ts` is the client-safe leaf that comment
// was waiting for — zero imports, so it cannot drag a node-only module into the
// browser bundle the way the original `chain-anchor` import did.
//
// So the local restatement is gone. It had become the shape this codebase keeps
// paying for: a UI copy of engine data that nothing forces to agree. The three
// values were identical the day they were written and nothing would have made a
// sound if they stopped being.

/* ══ 1. Workflow intake — the B1 magic moment ═════════════════════════════ */

/** A workflow document a real buyer would paste, used as the page's default. */
const SAMPLE_DOC = [
  "# Refund approval workflow",
  "",
  "When a support agent issues a refund over $5,000, a second reviewer must",
  "confirm the refund before it is submitted to the payment processor.",
  "",
  "The refund amount and currency are recorded on the ticket.",
  "Settlement is checked against the processor's own record the next day.",
].join("\n");

/**
 * The declared stand-in for the local-model compile step.
 *
 * This is NOT the spec compiler. It scans for the requirement families the real
 * compiler's kernel recognises and emits drafts whose `source_span` is a genuine
 * quotation from the document — so the grounding check downstream is doing real
 * work rather than being handed pre-validated input. It also emits one
 * deliberately ungrounded draft when the document does not mention settlement,
 * because a reviewer needs to see what a rejection looks like, and a demo where
 * nothing is ever rejected teaches the wrong thing.
 *
 * Every predicate it produces still has to survive `buildWorkflowIntakeView`.
 */
function standInCompile(doc: string): IntakeCompileOutcome {
  const trimmed = doc.trim();
  if (!trimmed) return { state: "fail-closed", reason: "empty-doc" };

  const quote = (re: RegExp): string | null => {
    const m = doc.match(re);
    return m ? m[0] : null;
  };

  const predicates: Extract<IntakeCompileOutcome, { state: "compiled" }>["predicates"] = [];
  const rejected: { draft: string; reason: string }[] = [];

  const confirmSpan = quote(/[^.\n]*\bconfirm\w*\b[^.\n]*/i);
  if (confirmSpan) {
    predicates.push({
      id: "p_confirm",
      kind: "checkpoint",
      tier: 1,
      expr: "exists(confirm_event) && confirm_event.actor != claim.author",
      falsifier: "an action of this kind with no confirm event by a different actor",
      source_span: confirmSpan.trim(),
    });
  }

  const amountSpan = quote(/[^.\n]*\b(amount|currency|\$[\d,]+)\b[^.\n]*/i);
  if (amountSpan) {
    predicates.push({
      id: "p_band",
      kind: "band",
      tier: 1,
      expr: "record.amount is present && record.currency is present",
      falsifier: "a record whose amount or currency is absent from the source system",
      source_span: amountSpan.trim(),
    });
  }

  const orderingSpan = quote(/[^.\n]*\bbefore\b[^.\n]*/i);
  if (orderingSpan) {
    predicates.push({
      id: "p_order",
      kind: "ordering",
      tier: 1,
      expr: "confirm_event.at < submit_event.at",
      falsifier: "a submission timestamped before its own confirmation",
      source_span: orderingSpan.trim(),
    });
  }

  const settlementSpan = quote(/[^.\n]*\bsettle\w*\b[^.\n]*/i);
  if (settlementSpan) {
    predicates.push({
      id: "p_settle",
      kind: "external-oracle",
      tier: 2,
      expr: "external.settlement_record matches record.amount",
      falsifier: "a settlement in the processor's record that disagrees with ours",
      source_span: settlementSpan.trim(),
    });
  } else {
    // The honest failure demo: a 7B asked for mechanical predicates will
    // confidently produce a settlement check whether or not the document asks
    // for one. Emitting it here — with a span the document does not contain —
    // lets the grounding check reject it in front of the reviewer.
    predicates.push({
      id: "p_settle_invented",
      kind: "external-oracle",
      tier: 2,
      expr: "external.settlement_record matches record.amount",
      falsifier: "a settlement in the processor's record that disagrees with ours",
      source_span: "settlement is reconciled against the processor nightly",
    });
  }

  if (predicates.length === 0) {
    rejected.push({
      draft: "(no mechanical predicate found)",
      reason: "the document names no checkpoint, threshold, ordering or external record",
    });
  }

  return {
    state: "compiled",
    predicates,
    rejected,
    model: "ollama/qwen2.5-coder:7b",
    attempts: 1,
  };
}

const FAIL_CLOSED_CHOICES: { value: "" | IntakeFailClosedReason; label: string }[] = [
  { value: "", label: "Local model present" },
  { value: "local-model-absent", label: "Local model absent" },
  { value: "frontier-forbidden", label: "Frontier model offered" },
  { value: "local-model-unparseable", label: "Model output unparseable" },
];

export function IntakePage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const [doc, setDoc] = useState(SAMPLE_DOC);
  const [oracle, setOracle] = useState("");
  const [failMode, setFailMode] = useState<"" | IntakeFailClosedReason>("");
  const [submitted, setSubmitted] = useState<WorkflowIntakeView | null>(null);
  const [bindError, setBindError] = useState<string | null>(null);

  const run = () => {
    setBindError(null);
    const outcome: IntakeCompileOutcome = failMode
      ? { state: "fail-closed", reason: failMode, detail: "simulated for review" }
      : standInCompile(doc);
    setSubmitted(
      buildWorkflowIntakeView({ doc, name: "Pasted workflow", outcome, boundOracle: oracle }),
    );
  };

  const bind = (key: string) => {
    if (!submitted) return;
    const result = bindOracleCandidate({ view: submitted, candidateKey: key });
    if (result.ok) {
      setSubmitted(result.view);
      setBindError(null);
      return;
    }
    setBindError(result.detail);
  };

  return (
    <PageShell
      title="Connect a workflow"
      lede="Paste what the workflow is supposed to guarantee. The engine derives the mechanical predicates and tells you whether it can be admitted."
    >

      <Callout tone="accent" title="What is real on this page">
        <p>
          The admission verdict, the grounding check, the oracle catalog and every
          refusal below are the shipped engine — <span className="vp-mono">buildWorkflowIntakeView</span>{" "}
          from <span className="vp-mono">@excellent/core</span>, the same function the product calls.
        </p>
        <p>
          The <strong>compile step is a declared stand-in</strong>. Real
          compilation runs a local model inside your boundary, which a browser
          cannot reach; this page scans your text for the requirement families the
          kernel recognises and quotes it back. Predicates still have to survive
          grounding, so a fabricated quotation is still rejected in front of you.
        </p>
      </Callout>

      <Section title="Your workflow document">
        <Card>
          <Fields>
            <Field label="Workflow description">
              <TextArea
                ariaLabel="Workflow description"
                value={doc}
                onChange={setDoc}
                placeholder="Describe what the workflow guarantees…"
              />
            </Field>
            <Field label="Independent oracle (optional)">
              <TextInput
                ariaLabel="Independent oracle"
                value={oracle}
                onChange={setOracle}
                placeholder="e.g. the payment processor's own refund record"
              />
            </Field>
            <Field label="Compile environment">
              <div style={{ display: "flex", flexWrap: "wrap", gap: "calc(var(--spacing) * 2)" }}>
                {FAIL_CLOSED_CHOICES.map((c) => (
                  <Button
                    key={c.value || "ok"}
                    variant={failMode === c.value ? "primary" : "outline"}
                    size="sm"
                    onClick={() => setFailMode(c.value)}
                  >
                    {c.label}
                  </Button>
                ))}
              </div>
            </Field>
          </Fields>
          <div style={{ marginTop: "calc(var(--spacing) * 4)" }}>
            <Button variant="primary" icon="play" onClick={run}>
              Derive predicates
            </Button>
          </div>
        </Card>
      </Section>

      {submitted ? <IntakeResult view={submitted} onBind={bind} bindError={bindError} onNavigate={onNavigate} /> : null}
    </PageShell>
  );
}

function IntakeResult({
  view,
  onBind,
  bindError,
  onNavigate,
}: {
  view: WorkflowIntakeView;
  onBind: (key: string) => void;
  bindError: string | null;
  onNavigate: (to: string) => void;
}) {
  const admissionTone =
    view.admission.status === "admit" ? "PASS" : view.admission.status === "reject" ? "FAIL" : "UNKNOWN";

  const predicateCols: Column<WorkflowIntakeView["predicates"][number]>[] = [
    { key: "id", header: "Predicate", render: (p) => <span className="vp-mono">{p.id}</span> },
    { key: "kind", header: "Kind", render: (p) => <Badge>{p.kind}</Badge> },
    { key: "expr", header: "Mechanical form", render: (p) => <Code>{p.expr}</Code> },
    {
      key: "falsifier",
      header: "What would falsify it",
      render: (p) => <span className="vp-row-sub">{p.falsifier}</span>,
    },
    {
      key: "span",
      header: "From your document",
      render: (p) => <span className="vp-row-sub">“{p.sourceSpan}”</span>,
    },
  ];

  return (
    <>
      <Section title="Admission verdict">
        <Card>
          <div style={{ display: "flex", alignItems: "center", gap: "calc(var(--spacing) * 3)", flexWrap: "wrap" }}>
            <Verdict status={admissionTone} />
            <strong>
              {view.admission.status === "admit"
                ? "Admitted — this workflow can be verified."
                : view.admission.status === "needs-oracle"
                  ? "Needs an independent oracle before it can be admitted."
                  : "Refused."}
            </strong>
          </div>
          <ul style={{ marginTop: "calc(var(--spacing) * 3)" }}>
            {view.admission.reasons.map((r) => (
              <li key={r} className="vp-mono vp-row-sub">
                {r}
              </li>
            ))}
          </ul>
          {view.model ? (
            <p className="vp-row-sub" style={{ marginTop: "calc(var(--spacing) * 2)" }}>
              Compiled by <span className="vp-mono">{view.model}</span> in {view.attempts}{" "}
              {view.attempts === 1 ? "attempt" : "attempts"}, over {view.docLength} characters.
            </p>
          ) : null}
        </Card>
      </Section>

      {view.blocked ? (
        <Section title="Compilation refused">
          <Callout tone="fail" title={`Fail-closed: ${view.blocked.reason}`}>
            <p>{view.blocked.message}</p>
            {view.blocked.fixCommand ? (
              <>
                <p>One command fixes it:</p>
                <Code>{view.blocked.fixCommand}</Code>
              </>
            ) : (
              <p>
                <strong>There is no fix command for this</strong>, deliberately. The
                refusal is the design, and offering a way around it would be
                offering a way to defeat the boundary.
              </p>
            )}
          </Callout>
        </Section>
      ) : null}

      {view.predicates.length > 0 ? (
        <Section
          title={`Derived predicates (${view.predicates.length})`}
          note="Each one is mechanical, and each names the observation that would prove it false."
        >
          <Table
            rows={view.predicates}
            columns={predicateCols}
            rowKey={(p) => p.id}
          />
        </Section>
      ) : null}

      {view.rejectedDrafts.length > 0 ? (
        <Section
          title={`Rejected drafts (${view.rejectedDrafts.length})`}
          note="Shown rather than dropped: what the model proposed and could not support is evidence about the model."
        >
          <Card>
            {view.rejectedDrafts.map((r, i) => (
              <div key={`${r.reason}-${i}`} style={{ marginBottom: "calc(var(--spacing) * 3)" }}>
                <div>
                  <Badge tone="fail">{r.reason}</Badge>{" "}
                  <span className="vp-mono">{r.draft}</span>
                </div>
                {r.inventedSpan ? (
                  <p className="vp-row-sub">
                    It attributed this phrase to your document, which does not contain
                    it: “{r.inventedSpan}”
                  </p>
                ) : null}
                {r.detail ? <p className="vp-row-sub">{r.detail}</p> : null}
              </div>
            ))}
          </Card>
        </Section>
      ) : null}

      {view.oracleCandidates.length > 0 ? (
        <Section
          title="Bind an independent oracle"
          note="Nothing is admitted without one. These are the oracles that could discharge this workflow's claims."
        >
          {bindError ? (
            <Callout tone="fail" title="That oracle cannot be bound">
              <p>{bindError}</p>
            </Callout>
          ) : null}
          <Grid cols={2}>
            {view.oracleCandidates.map((c) => (
              <Card key={c.key} title={c.key}>
                <p className="vp-row-sub">{c.proves}</p>
                <p className="vp-row-sub">
                  <strong>To defeat it you would have to forge:</strong> {c.forge}
                </p>
                {c.bindable ? (
                  <Button variant="outline" size="sm" onClick={() => onBind(c.key)}>
                    Bind this oracle
                  </Button>
                ) : (
                  <>
                    <Badge tone="unknown">not available yet</Badge>
                    <p className="vp-row-sub">{c.unbindableReason}</p>
                  </>
                )}
              </Card>
            ))}
          </Grid>
        </Section>
      ) : null}

      {view.admission.status === "admit" ? (
        <Section title="Next">
          <Card>
            <p>
              This workflow is admitted. The engine can now run it, and every FAIL
              or UNKNOWN it produces routes to a human with a written rationale.
            </p>
            <Button variant="outline" onClick={() => onNavigate(href("queue"))}>
              Go to the decision inbox
            </Button>
          </Card>
        </Section>
      ) : null}
    </>
  );
}

/* ══ 2. API surface — B2 ══════════════════════════════════════════════════ */

export function ApiSurfacePage() {
  const rows = useMemo(() => describeVerificationApiSurface(), []);
  const withheld = rows.filter((r) => !r.served);

  const cols: Column<(typeof rows)[number]>[] = [
    { key: "id", header: "Operation", render: (r) => <span className="vp-mono">{r.id}</span> },
    { key: "method", header: "Method", render: (r) => <Badge>{r.method}</Badge> },
    { key: "path", header: "Path", render: (r) => <Code>{r.path}</Code> },
    { key: "scope", header: "Scope", render: (r) => <span className="vp-mono">{r.requiredScope}</span> },
    {
      key: "served",
      header: "Served",
      render: (r) =>
        r.served ? <Badge tone="pass">served</Badge> : <Badge tone="fail">engine-only</Badge>,
    },
  ];

  return (
    <PageShell
      title="API & SDK"
      lede="The served endpoints, their scopes, and the one operation deliberately withheld from them."
      wide
    >

      <Grid cols={3}>
        <Stat label="Endpoints in catalogue" value={String(rows.length)} />
        <Stat label="Served" value={String(PUBLIC_VERIFICATION_API_ENDPOINTS.length)} />
        <Stat label="Engine-only" value={String(withheld.length)} tone="fail" />
      </Grid>

      {withheld.map((r) => (
        <Callout key={r.id} tone="fail" title={`${r.id} is not served — and that is the point`}>
          <p>{r.withheldReason}</p>
          <p className="vp-row-sub">
            Refused before authentication, so no scope reaches it —{" "}
            <span className="vp-mono">verification:*</span> included. It is listed here rather than
            hidden: a surface that quietly omitted it would look like a
            documentation gap instead of a security decision.
          </p>
        </Callout>
      ))}

      <Section
        title="Endpoints"
        note="Every write requires an Idempotency-Key. A replay returns the original object; a key reused with a different body is refused."
      >
        <Table rows={rows} columns={cols} rowKey={(r) => r.id} />
      </Section>

      <Section title="What the API will not do">
        <Card>
          <ul>
            <li>
              <strong>No ambient authority.</strong> Reads need a token too.
            </li>
            <li>
              <strong>A cross-tenant request answers 404, never 403.</strong> A 403
              would confirm the object exists in someone else's tenant.
            </li>
            <li>
              <strong>A write without an Idempotency-Key is refused</strong> rather
              than applied once and hoped about.
            </li>
            <li>
              <strong>A rejected request does not consume its key</strong>, so a
              caller who fixes a scope can retry.
            </li>
            <li>
              <strong>{Object.keys(ENGINE_ONLY_OPERATIONS).length} operation(s) are
              engine-only.</strong> Receipts are minted by the engine over a run,
              never by a token.
            </li>
          </ul>
        </Card>
      </Section>
    </PageShell>
  );
}

/* ══ 3. Security posture — B4 ═════════════════════════════════════════════ */

export function PosturePage() {
  const coverage = useMemo(() => postureCoverage(), []);
  const questions = VERIFICATION_POSTURE_QUESTIONS;

  return (
    <PageShell
      title="Security posture"
      lede="The enterprise security questionnaire, answered here so it does not need a call. Unanswered rows are shown as unanswered."
    >

      <Grid cols={4}>
        <Stat label="Questions" value={String(coverage.total)} />
        <Stat label="Answered" value={String(coverage.answered)} tone="pass" />
        <Stat label="Answered with caveat" value={String(coverage.caveated)} tone="unknown" />
        <Stat label="Not yet" value={String(coverage.notYet)} tone="fail" />
      </Grid>

      {coverage.notYet > 0 ? (
        <Callout tone="fail" title={`${coverage.notYet} question(s) we cannot answer yet`}>
          <p>
            These are listed rather than omitted. An unanswered row is a fact about
            the product; a missing row is a fact about the page.
          </p>
        </Callout>
      ) : null}

      {questions.map((q) => (
        <Section key={q.id} title={q.question}>
          <Card>
            <div style={{ marginBottom: "calc(var(--spacing) * 2)" }}>
              {q.status === "answered" ? (
                <Badge tone="pass">answered</Badge>
              ) : q.status === "answered-with-caveat" ? (
                <Badge tone="unknown">answered, with a stated ceiling</Badge>
              ) : (
                <Badge tone="fail">not yet</Badge>
              )}{" "}
              <span className="vp-mono vp-row-sub">{q.id}</span>
            </div>
            <p>{q.answer}</p>
            {q.caveat ? (
              <Callout tone="unknown" title="The ceiling on that answer">
                <p>{q.caveat}</p>
              </Callout>
            ) : null}
            {q.runtimeModes.length > 0 ? (
              <p className="vp-row-sub">
                Varies by runtime mode: {q.runtimeModes.join(", ")}
              </p>
            ) : null}
          </Card>
        </Section>
      ))}
    </PageShell>
  );
}

/* ══ 4. Buyable scoreboard — the run's own report card ════════════════════ */

type GateResultsArtifact = {
  generatedAt?: string;
  results?: Record<string, string>;
};

export const GATE_RESULTS_MAX_AGE_MS = 86400000;

export function resolveGateResults(
  artifact: GateResultsArtifact | null | undefined = gateResultsArtifact,
  now = Date.now(),
): Record<string, GateOutcome> {
  if (!artifact?.results || !artifact.generatedAt) return {};
  const stamped = Date.parse(artifact.generatedAt);
  if (!Number.isFinite(stamped)) return {};
  if (now - stamped > GATE_RESULTS_MAX_AGE_MS) return {};
  const out: Record<string, GateOutcome> = {};
  for (const gate of allBuyableGateNames()) {
    const value = artifact.results[gate];
    if (value === "pass" || value === "fail" || value === "not-run") {
      out[gate] = value;
    }
  }
  return out;
}

export function BuyablePage() {
  const evaluations = useMemo(
    () => evaluateBuyableCriteria({ gateResults: resolveGateResults(), demonstrated: [] }),
    [],
  );
  const verdict = useMemo(() => isBuyable(evaluations), [evaluations]);
  const pendingCount = Object.keys(PENDING_GATES).length;

  return (
    <PageShell
      title="Is it buyable?"
      lede="The five things a stranger must be able to do, and what protects each one."
    >

      <Callout tone={verdict.buyable ? "accent" : "fail"} title={verdict.buyable ? "Buyable" : "Not buyable yet"}>
        <p>
          {verdict.buyable
            ? "Every criterion has its gates green and its demonstration recorded."
            : `${verdict.short.length} of ${evaluations.length} criteria are short: ${verdict.short.join(", ")}.`}
        </p>
        <p className="vp-row-sub">
          <strong>A green gate is not a buy.</strong> Each criterion is about what a
          stranger can do; a gate over a module proves the logic and says nothing
          about the stranger. So a criterion whose gates all pass reads{" "}
          <span className="vp-mono">gates-green</span>, not <span className="vp-mono">met</span>, until the demonstration is
          actually performed. {pendingCount} gate(s) are not yet written, and the
          engine reports those as not-run whatever this page claims.
        </p>
      </Callout>

      {evaluations.map((e) => {
        const c = BUYABLE_CRITERIA.find((x) => x.id === e.id);
        if (!c) return null;
        const tone =
          e.status === "met" ? "pass" : e.status === "unmet" ? "fail" : e.status === "gates-green" ? "unknown" : "unknown";
        return (
          <Section key={e.id} title={`${c.id} — ${c.statement}`}>
            <Card>
              <div style={{ marginBottom: "calc(var(--spacing) * 2.5)" }}>
                <Badge tone={tone as "pass" | "fail" | "unknown"}>{e.status}</Badge>{" "}
                <span className="vp-row-sub">
                  converts: {c.personas.join(", ")} · units: {c.units.join(", ")}
                </span>
              </div>

              <p className="vp-row-sub">
                <strong>Gates:</strong>{" "}
                {c.gates.map((g) => (
                  <span key={g} className="vp-mono" style={{ marginRight: "calc(var(--spacing) * 2.5)" }}>
                    {g}
                    {g in PENDING_GATES ? " (not written yet)" : ""}
                  </span>
                ))}
              </p>

              <p>
                <strong>Still needs a human to demonstrate:</strong> {c.demonstration}
              </p>

              {c.caveats.length > 0 ? (
                <Callout tone="unknown" title="Stated ceilings on this claim">
                  <ul>
                    {c.caveats.map((cv) => (
                      <li key={cv.slice(0, 24)}>{cv}</li>
                    ))}
                  </ul>
                </Callout>
              ) : null}
            </Card>
          </Section>
        );
      })}

      <Section
        title="Buyer-gauntlet blockers"
        note="From the six-persona review. Note which ones no gate can close."
      >
        <Card>
          {GAUNTLET_BLOCKERS.map((b) => (
            <div key={b.id} style={{ marginBottom: "calc(var(--spacing) * 2.5)" }}>
              <Badge tone={b.closure === "human" ? "unknown" : "accent"}>{b.id}</Badge>{" "}
              <strong>{b.closure === "human" ? "needs a person" : "gate-closable"}</strong>{" "}
              <span className="vp-row-sub">
                {b.summary} — units {b.units.join(", ")}
              </span>
            </div>
          ))}
          <p className="vp-row-sub">
            F5 requires publishing a pack. Publishing is an act, not a code path —
            no gate can perform it, and marking it machine-closable would let a
            passing script claim a public artifact exists.
          </p>
        </Card>
      </Section>

      <Section title="All gates this scoreboard depends on">
        <Card>
          <Code>{allBuyableGateNames().join("\n")}</Code>
          <p className="vp-row-sub">
            <span className="vp-mono">test-verification-buyable-criteria</span> asserts every name here
            is registered in the repo's gate registry, or declared pending with the
            unit that owes it. A criterion cannot claim protection from a gate that
            does not exist.
          </p>
        </Card>
      </Section>
    </PageShell>
  );
}

/* ══ 5. Trust root — B3 ═══════════════════════════════════════════════════ */

/**
 * The trust-root page.
 *
 * This is the page the gauntlet's CISO persona reads, and it exists because she
 * found two counterexamples to "verifiable without trusting you or us" in under
 * an hour: the offline verifier and the signing keys both shipped *inside* the
 * pack they validate, and the hash chains had no external anchor.
 *
 * Both are now closed — and the page's job is to be equally clear about the two
 * ceilings that are *not* closed, both surfaced by adversarial review during this
 * run. It renders `packTrustRootAssuranceCeiling` and
 * `chainAnchorRollbackCoverage` verbatim, which is the point: the honest
 * sentence lives in the engine, so the page cannot soften it and a gate can
 * assert it.
 */
export function TrustPage() {
  const keyCeiling = useMemo(() => packTrustRootAssuranceCeiling("anchored"), []);
  // The rollback ceiling, taken from the engine's own posture answer rather than
  // restated here. `anchoring-external` is `answered-with-caveat` and its caveat
  // is the sentence a gate asserts must mention rollback and freshness.
  const anchorPosture = useMemo(
    () => VERIFICATION_POSTURE_QUESTIONS.find((q) => q.id === "anchoring-external") ?? null,
    [],
  );

  return (
    <PageShell
      title="Trust root"
      lede="What an outside party can check without trusting us — and precisely where that stops."
    >

      <Section title="Closed: the pack no longer attests to itself">
        <Grid cols={2}>
          <Card title="Verifier obtained out-of-band">
            <p>
              The offline verifier is distributed independently of the pack as a
              reproducibly built artifact. The pack carries data only. A pack that
              shipped its own verifier could ship a lying one.
            </p>
            <Badge tone="pass">closed</Badge>
          </Card>
          <Card title="Key fingerprints published outside the pack">
            <p>
              Signing-key fingerprints are published on the customer's own domain
              and on the Runtime &amp; keys page. When a verifier can only reach
              in-pack keys it reports <span className="vp-mono">degraded</span> explicitly rather than
              passing quietly, and a fingerprint that contradicts the out-of-band
              set is <span className="vp-mono">rejected</span> — not degraded.
            </p>
            <Badge tone="pass">closed</Badge>
          </Card>
        </Grid>
      </Section>

      <Section title="Anchoring targets">
        <Card>
          <ul>
            {CHAIN_ANCHOR_TARGETS.map((t) => (
              <li key={t}>
                <span className="vp-mono">{t}</span>
              </li>
            ))}
          </ul>
          <p className="vp-row-sub">
            Key sources: {OUT_OF_BAND_KEY_SOURCES.map((s) => s).join(", ")}.{" "}
            <span className="vp-mono">local-offline</span> uses the operator-held file, hand-carried
            like the packs, so the verify path makes no network call.
          </p>
        </Card>
      </Section>

      <Section
        title="Not closed: the two stated ceilings"
        note="Found by adversarial review during this run. They are here because a reviewer who discovers an omission stops believing the rest of the page."
      >
        <Callout tone="unknown" title="Symmetric signing bounds what anchoring can prove">
          <p>{keyCeiling.statement}</p>
          <p className="vp-row-sub">
            <span className="vp-mono">{`independentOfVendor = ${String(keyCeiling.independentOfVendor)}`}</span>{" "}
            in <strong>every</strong> mode, <span className="vp-mono">anchored</span> included — which
            is exactly where the overclaim would be tempting. Reaching "verifiable
            without trusting us" requires asymmetric signing, which is not shipped.
          </p>
        </Callout>
        <Callout tone="unknown" title="Rollback is not detectable from inside a pack">
          <p>{anchorPosture?.caveat ?? ""}</p>
          <p>
            <strong>What would make it detectable:</strong> the reader holding the
            expected anchor id independently of the pack — supplied out-of-band,
            the same way the key fingerprints are.
          </p>
          <p className="vp-row-sub">
            Truncation and rollback are different attacks. An anchor cannot prove
            its own freshness any more than a pack can prove its own key.
          </p>
        </Callout>
      </Section>

      <Section title="How this is tested">
        <Card>
          <p>
            The adversarial gate is written as an attacker: five attacks that must
            each be <em>detected</em>, plus a clean-pack control, because an
            always-reject verifier would pass an attacks-only gate vacuously.
          </p>
          <ul>
            <li>Tampered evidence, chain untouched.</li>
            <li>
              Tampered evidence <strong>with the chain regenerated</strong> to be
              internally consistent — only the external anchor catches this one.
            </li>
            <li>
              Swapped signing key with a self-consistent in-pack keyset — only the
              out-of-band fingerprints catch this one.
            </li>
            <li>Truncated ledger.</li>
            <li>Replayed anchor from an earlier honest state.</li>
          </ul>
          <p className="vp-row-sub">
            The clean-pack control is built from a committed fixture rather than a
            live generator call, so a bug in the generator cannot make both halves
            agree and prove nothing.
          </p>
        </Card>
      </Section>
    </PageShell>
  );
}

/* ══ 6. Shared empty state for surfaces awaiting a unit ═══════════════════ */

export function AwaitingUnitPage({ unit, what }: { unit: string; what: string }) {
  return (
    <PageShell title={what}>
      <Empty title={`${what} is not built yet`}>
        {`${unit} owes this surface. It is listed in the rail rather than hidden so the gap is visible in the product, not only in a plan.`}
      </Empty>
    </PageShell>
  );
}
